import type { App } from 'vue';
import type { Router } from 'vue-router';
import { isChunkLoadError } from './normalizeError';
import { ErrorKind, ErrorSource, ErrorTier, type NormalizeContext } from './errorTypes';
import { redactFullPath } from './redact';
import { pushTrail } from './trail';

/**
 * The four capture points the app was missing entirely: Vue render errors, unhandled rejections,
 * uncaught window errors, and router failures.
 *
 * The handler is injected rather than imported, so this module stays free of Pinia — `main.ts` passes
 * a closure over `useErrorStore().handle`. Every listener is wrapped: a fault here must degrade to a
 * console line, never escalate.
 */

export interface GlobalHandlerHooks {
  handle: (error: unknown, context: NormalizeContext) => void;
}

/**
 * Vue hands us a description of where the error came from. The distinction that matters is whether the
 * subtree is structurally broken or merely misbehaved: a click handler throwing must not blank the app.
 */
const STRUCTURAL_INFO = ['setup function', 'render function', 'scheduler flush', 'nextTick'];

const isStructural = (info: string): boolean =>
  STRUCTURAL_INFO.some(needle => info.includes(needle)) || info.includes('hook');

/** Vue-router cancels navigation by rejecting; that is flow control, not a failure. */
const isNavigationFailure = (value: unknown): boolean =>
  value != null && typeof value === 'object' && 'type' in value && 'to' in value && 'from' in value;

const CHUNK_RELOAD_KEY = 'spdata.chunkReloadAt';
const CHUNK_RELOAD_WINDOW_MS = 10000;

/**
 * A stale deploy needs a reload, but reloading unconditionally is the classic deploy-rollover infinite
 * loop. Reload at most once per window; after that, show the screen and let the user decide.
 */
const tryChunkReload = (): boolean => {
  try {
    const previous = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    if (Date.now() - previous < CHUNK_RELOAD_WINDOW_MS) {
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
};

export const installGlobalErrorHandlers = (app: App, router: Router, hooks: GlobalHandlerHooks): void => {
  // `capture: false` everywhere below is implicit: `handle` decides reportability from the taxonomy,
  // and Sentry's own global handlers are switched off in sentry.ts so nothing double-reports.
  app.config.errorHandler = (error, instance, info) => {
    try {
      const structural = isStructural(info);
      hooks.handle(error, {
        source: ErrorSource.RENDER,
        // Only a structural failure is a *render* failure. An event handler throwing is not, and
        // labelling it one puts "This screen could not load" over a screen that loaded fine.
        kind: structural ? ErrorKind.RENDER : ErrorKind.UNKNOWN,
        tier: structural ? ErrorTier.FATAL : ErrorTier.TOAST
      });
    } catch (handlerError) {
      /* eslint-disable-next-line no-console */
      console.error('[errors] vue handler failed', handlerError);
    }
  };

  window.addEventListener('unhandledrejection', event => {
    try {
      if (isNavigationFailure(event.reason)) {
        return;
      }
      hooks.handle(event.reason, {
        source: ErrorSource.REJECTION,
        kind: isChunkLoadError(event.reason) ? ErrorKind.CHUNK_LOAD : undefined
      });
    } catch (handlerError) {
      /* eslint-disable-next-line no-console */
      console.error('[errors] rejection handler failed', handlerError);
    }
  });

  window.addEventListener('error', event => {
    try {
      // Resource-load failures (a broken <img>, a blocked <script>) fire here with `error == null`.
      // Without this guard every failed avatar would raise an error screen.
      if (!(event.error instanceof Error)) {
        return;
      }
      hooks.handle(event.error, { source: ErrorSource.GLOBAL });
    } catch (handlerError) {
      /* eslint-disable-next-line no-console */
      console.error('[errors] window error handler failed', handlerError);
    }
  });

  router.onError(error => {
    try {
      if (isChunkLoadError(error) && tryChunkReload()) {
        return;
      }
      hooks.handle(error, {
        source: ErrorSource.ROUTER,
        kind: isChunkLoadError(error) ? ErrorKind.CHUNK_LOAD : ErrorKind.ROUTE,
        tier: ErrorTier.FATAL
      });
    } catch (handlerError) {
      /* eslint-disable-next-line no-console */
      console.error('[errors] router error handler failed', handlerError);
    }
  });

  router.afterEach(to => {
    pushTrail({ type: 'route', label: redactFullPath(to.fullPath) });
  });
};
